import type { Locale } from "@scalephant/contract";
import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from "react";

import { MESSAGES, type MessageKey } from "./messages";

export type { Locale };

const STORAGE_KEY = "scalephant.locale";

export function detectLocale(): Locale {
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY);
    if (stored === "fi" || stored === "en") return stored;
  } catch {
    // Storage may be unavailable (private mode); fall through to the browser language.
  }
  return navigator.language.toLowerCase().startsWith("fi") ? "fi" : "en";
}

export type Translate = (
  key: MessageKey,
  params?: Readonly<Record<string, string | number>>,
) => string;

interface I18nContextValue {
  readonly locale: Locale;
  readonly t: Translate;
  readonly setLocale: (locale: Locale) => void;
}

const I18nContext = createContext<I18nContextValue | null>(null);

export function translate(
  locale: Locale,
  key: MessageKey,
  params?: Readonly<Record<string, string | number>>,
): string {
  const template = MESSAGES[locale][key];
  if (!params) return template;
  return template.replaceAll(/\{(\w+)\}/g, (whole, name: string) => {
    const value = params[name];
    return value === undefined ? whole : String(value);
  });
}

export function I18nProvider({
  children,
  initialLocale,
}: {
  children: ReactNode;
  initialLocale?: Locale;
}) {
  const [locale, setLocaleState] = useState<Locale>(() => initialLocale ?? detectLocale());
  const setLocale = useCallback((next: Locale) => {
    setLocaleState(next);
    try {
      window.localStorage.setItem(STORAGE_KEY, next);
    } catch {
      // Best effort; the profile is the durable source once signed in.
    }
    document.documentElement.lang = next;
  }, []);
  const value = useMemo<I18nContextValue>(
    () => ({ locale, setLocale, t: (key, params) => translate(locale, key, params) }),
    [locale, setLocale],
  );
  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useI18n(): I18nContextValue {
  const value = useContext(I18nContext);
  if (!value) throw new Error("useI18n must be used inside I18nProvider");
  return value;
}

export function useT(): Translate {
  return useI18n().t;
}

/** Turns a zod issue message (a validation key) into UI text; unknown messages pass through. */
export function validationMessage(
  t: Translate,
  message: string | undefined,
  params?: Readonly<Record<string, string | number>>,
): string | undefined {
  if (message === undefined) return undefined;
  const key = `validation.${message}` as MessageKey;
  return key in MESSAGES.en ? t(key, params) : message;
}
